import { DependencyList, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsNative, { useIsNativeVersionSupported } from './useIsNative';

export type NativeBridgeMessage = {
	key:
	| 'link.open'
	| 'camera.show'
	| 'camera.ready'
	| 'camera.setFocus'
	| 'camera.isFocusSuccessful'
	| 'camera.takePicture'
	| 'camera.snapshotTaken'
	| 'camera.pictureTaken'
	| 'camera.setZoom'
	| 'recorder.start'
	| 'recorder.stop'
	| 'recorder.error'
	| 'recorder.recordingTaken'
	| 'keyValue.set'
	| 'keyValue.setAll'
	| 'keyValue.get'
	| 'keyValue.getAll'
	| 'storage.listFiles'
	| 'storage.saveFileBase64'
	| 'storage.readFileBase64'
	| 'storage.readImageThumbnailBase64'
	| 'storage.deleteFile'
	| 'share.file'
	| 'haptics.impact'
	| 'haptics.notification'
	| 'tts.speak'
	| 'tts.spoken'
	| 'alert.show'
	| 'alert.confirm'
	| 'notifications.checkPermission'
	| 'notifications.requestPermission'
	| 'notifications.list'
	| 'notifications.schedule';
	value?: string;
};

// Split a key-value pair separated by '='
export const isStringAndKeyValue = (data: unknown) =>
	typeof data === 'string' || (data as string).includes('=');
export const splitKeyValue = (data: string): NativeBridgeMessage => ({
	key: data.split('=')[0] as NativeBridgeMessage['key'],
	value: data.split('=').slice(1).join('='),
});

// Callback for handling native messages
export type NativeMessageCallback = (
	key: NativeBridgeMessage['key'],
	value?: NativeBridgeMessage['value'],
) => void;
export const useNativeMessageCallback = (
	onMessage: NativeMessageCallback,
	deps: DependencyList,
) => useCallback(onMessage, [onMessage, ...deps]);

// Add native event listeners
const addNativeEventListeners = (onMessage: (event: MessageEvent) => void) => {
	window.addEventListener('message', onMessage);
	// @ts-expect-error this is for Android support
	document.addEventListener('message', onMessage);
};

// Remove native event listeners
const removeNativeEventListeners = (
	onMessage: (event: MessageEvent) => void,
) => {
	window.removeEventListener('message', onMessage);
	// @ts-expect-error this is for Android support
	document.removeEventListener('message', onMessage);
};

const useNativeMessages = (
	onMessage?: NativeMessageCallback,
	// Filter messages by key (required if onMessage is set)
	acceptMessageKeys?: NativeBridgeMessage['key'][],
) => {
	const navigate = useNavigate();

	const isNative = useIsNative();
	const isNativeAndIsVersionSupported = useIsNativeVersionSupported();
	const acceptMessageKeysSet = useMemo(
		() => new Set(acceptMessageKeys ?? []),
		[acceptMessageKeys],
	);

	// Check if the app is running in a native environment and if the native version is supported
	useEffect(() => {
		if (!isNativeAndIsVersionSupported) {
			navigate('/unsupported');
		}
	}, [isNativeAndIsVersionSupported, navigate]);

	const onMessageInternal = useCallback(
		(event: MessageEvent) => {
			// Check if the app is running in a native environment, if an onMessage callback is provided and if the message is a key-value pair
			if (!isNative || !onMessage || !isStringAndKeyValue(event.data)) {
				return;
			}

			// Split the key and value from the message
			const { key, value } = splitKeyValue(event.data);

			// Check if the key is accepted
			if (!acceptMessageKeysSet.has(key)) {
				return;
			}

			// Call the onMessage callback with the key and value
			onMessage(key, value);
		},
		[isNative, onMessage, acceptMessageKeysSet],
	);

	// Add and remove native event listeners
	useEffect(() => {
		addNativeEventListeners(onMessageInternal);
		return () => {
			removeNativeEventListeners(onMessageInternal);
		};
	}, [onMessageInternal]);

	// Send a native message with a specific key and value
	const sendNativeMessage: NativeMessageCallback = useCallback(
		(
			key: NativeBridgeMessage['key'],
			value: NativeBridgeMessage['value'],
		) => {
			// Throw error if not connected to native app
			if (!isNative) {
				throw new Error('Not connected to native app');
			}

			window.ReactNativeWebView?.postMessage(
				`${key}=${value !== undefined && value.length > 0 ? value : ''}`,
			);
		},
		[isNative],
	);

	// Wait for a single native message with a specific key
	const awaitSingleNativeMessage = useCallback(
		async (key: NativeBridgeMessage['key'], startsWithFilter = '') => {
			// Throw error if not connected to native app
			if (!isNative) {
				throw new Error('Not connected to native app');
			}

			return new Promise<string>((resolve) => {
				const onSingleMessage = (event: MessageEvent) => {
					// Check if the app is running in a native environment, if the message is a key-value pair and if the key matches
					if (!isNative || !isStringAndKeyValue(event.data)) {
						return;
					}

					// Split the key and value from the message and check key
					const { key: messageKey, value } = splitKeyValue(
						event.data,
					);
					if (
						(messageKey === key && startsWithFilter.length < 1) ||
						(value && value.startsWith(startsWithFilter))
					) {
						resolve(value ?? '');
						removeNativeEventListeners(onSingleMessage);
					}
				};

				addNativeEventListeners(onSingleMessage);

				// Auto remove listener after 15 seconds
				setTimeout(() => {
					removeNativeEventListeners(onSingleMessage);
				}, 15e3);
			});
		},
		[isNative],
	);

	return {
		sendNativeMessage,
		awaitSingleNativeMessage,
	};
};

export default useNativeMessages;
